import HTMLReactParser from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import {
   Badge,
   Box,
   Button,
   Card,
   Flex,
   Heading,
   Separator,
   Text,
   Tooltip,
   Dialog,
} from '@radix-ui/themes';

import { CheckIcon, InfoIcon } from 'shared/icons';
import { PurchasePlan } from '../plans-purchase.types';
import { PlanPurchaseDialog } from './purchase-dialog';
import { useState } from 'react';
import { PromoPlanDialog } from './promo-plan-dialog';

type PlanCardProps = {
   plan: PurchasePlan;
   currency: string;
   primaryColor: string;
   secondaryColor: string;
   onAuthError: () => void;
   onError: (message: string) => void;
   onSuccess: (message: string) => void;
};

export const normalizePlanAmount = (amount: PurchasePlan['amount']): number => {
   const value = Number(amount);
   return Number.isFinite(value) ? value : 0;
};

const renderPrice = (plan: PurchasePlan, currency: string): string => {
   const planCurrency = currency || plan.currency || '';

   return `${planCurrency} ${normalizePlanAmount(plan.amount).toLocaleString('uk-UA')}`.trim();
};

const renderPeriod = (plan: PurchasePlan, t: (key: string) => string): string => {
   const periodValue = String(plan.plan_type ?? '');

   if (plan.plan_type_value === 'Months') {
      return `${periodValue} ${
         String(plan.plan_type) === '1' ? t('membershipPlan.month') : t('membershipPlan.months')
      }`;
   }

   if (plan.plan_type_value === 'Years') {
      return `${periodValue} ${
         String(plan.plan_type) === '1' ? t('membershipPlan.year') : t('membershipPlan.years')
      }`;
   }

   return [periodValue, plan.plan_type_value].filter(Boolean).join(' ');
};

export function PlanCard({ plan, currency, primaryColor, secondaryColor }: PlanCardProps) {
   const { t } = useTranslation('global');
   const active = plan.is_plan_active === 1;
   const freePlan = normalizePlanAmount(plan.amount) <= 0;
   const features = plan.features.filter(
      (feature) => !!feature.outer?.trim() || !!feature.inner?.trim()
   );

   const [openDialog, setOpenDialog] = useState(false);

   return (
      <>
         <Card className="plansPurchaseCard" size="3">
            <Flex direction="column" height="100%">
               <Box className="plansPurchaseCardTop">
                  <Flex justify="between" align="start" gap="3">
                     <Heading as="h4" size="4" className="plansPurchasePlanName">
                        {plan.plan_name}
                     </Heading>

                     {active && (
                        <Badge color="blue" variant="soft">
                           {t('membershipPlan.currentPlanButton')}
                        </Badge>
                     )}
                  </Flex>

                  <Heading
                     as="h2"
                     size="8"
                     className="plansPurchasePrice"
                     style={{ color: primaryColor }}
                  >
                     {renderPrice(plan, currency)}
                  </Heading>

                  <Text as="p" color="gray" className="plansPurchasePeriod">
                     {renderPeriod(plan, t)}
                  </Text>
               </Box>

               <Dialog.Root
                  open={openDialog}
                  onOpenChange={(o) => {
                     setOpenDialog(o);
                  }}
               >
                  <Button
                     className="plansPurchaseAction"
                     type="button"
                     size="3"
                     radius="small"
                     disabled={active}
                     onClick={() => {
                        setOpenDialog(true);
                     }}
                     style={
                        active
                           ? {
                                cursor: 'default',
                                borderColor: secondaryColor,
                                backgroundColor: secondaryColor,
                             }
                           : {
                                borderColor: primaryColor,
                                backgroundColor: primaryColor,
                             }
                     }
                  >
                     {active
                        ? t('membershipPlan.currentPlanButton')
                        : freePlan
                          ? t('membershipPlan.applyThisPlan')
                          : t('membershipPlan.buyThisPlan')}
                  </Button>

                  {plan.amount === 0 && (
                     <PromoPlanDialog
                        plan={plan}
                        open={openDialog}
                        setOpen={(o) => {
                           setOpenDialog(o);
                        }}
                     ></PromoPlanDialog>
                  )}

                  {plan.amount > 0 && (
                     <PlanPurchaseDialog
                        plan={plan}
                        open={openDialog}
                        setOpen={(o) => {
                           setOpenDialog(o);
                        }}
                     ></PlanPurchaseDialog>
                  )}
               </Dialog.Root>

               <Separator size="4" my="5" />
               <Box asChild className="plansPurchaseFeatures">
                  <ul>
                     {features.map((feature, index) => (
                        <li key={`${plan.plan_id}-${index}`}>
                           <Text as="span" color="green" className="plansPurchaseCheck">
                              <CheckIcon width={18} height={18} />
                           </Text>

                           <Text as="span" className="plansPurchaseFeatureText">
                              {feature.outer ? HTMLReactParser(feature.outer) : ''}
                           </Text>

                           {feature.inner && (
                              <Tooltip
                                 content={
                                    <Text as="span" size="2">
                                       {HTMLReactParser(feature.inner)}
                                    </Text>
                                 }
                              >
                                 <Text
                                    as="span"
                                    color="blue"
                                    highContrast
                                    className="plansPurchaseInfo"
                                 >
                                    <InfoIcon width={18} height={18} />
                                 </Text>
                              </Tooltip>
                           )}
                        </li>
                     ))}
                  </ul>
               </Box>
            </Flex>
         </Card>
      </>
   );
}
